import { Outlet, NavLink } from 'react-router-dom'
import { useAuthStore } from '../../hooks/useAuth'
import { useState, Suspense } from 'react'
import { GraduationCap, BookOpen, Menu, LogOut, UserCircle } from 'lucide-react'
import clsx from 'clsx'

function ContentLoader() {
  return (
    <div className="flex-1 flex items-center justify-center py-24">
      <div className="flex flex-col items-center gap-3">
        <div className="w-8 h-8 border-4 border-aqua-200 border-t-aqua-500 rounded-full animate-spin" />
        <p className="text-sm text-gray-400 font-semibold">Cargando...</p>
      </div>
    </div>
  )
}

const NAV = [
  { label: 'Capacitación', to: '/capacitador/capacitacion', icon: GraduationCap },
  { label: 'AquaLeern',    to: '/capacitador/aqualeern',    icon: BookOpen },
]

export default function CapacitadorLayout() {
  const { user, logout } = useAuthStore()
  const [open, setOpen] = useState(false)

  return (
    <div className="flex min-h-screen bg-gray-50">
      {/* Sidebar desktop */}
      <aside className="hidden md:flex flex-col w-56 bg-white border-r border-gray-100 py-6 px-3 gap-1 fixed h-full z-30">
        <div className="flex items-center gap-2 px-3 mb-6">
          <img src="/logo.png" alt="Aqua" className="h-8 w-auto" onError={e => { (e.target as HTMLImageElement).style.display = 'none' }} />
          <span className="font-bold text-gray-800 text-sm">Aqua Capacitador</span>
        </div>

        {NAV.map(item => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) =>
              clsx('flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all',
                isActive
                  ? 'bg-aqua-50 text-aqua-700 font-semibold'
                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900')
            }
          >
            <item.icon size={18} />
            {item.label}
          </NavLink>
        ))}

        <div className="mt-auto pt-4 border-t border-gray-100 space-y-1">
          <div className="flex items-center gap-2 px-3 py-2">
            <UserCircle size={16} className="text-gray-400" />
            <div className="min-w-0">
              <p className="text-xs font-semibold text-gray-700 truncate">{user?.nombre ?? 'Capacitador'}</p>
              <p className="text-[10px] text-gray-400 truncate">{user?.email}</p>
            </div>
          </div>
          <button
            onClick={logout}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-sm text-red-500 hover:bg-red-50 transition"
          >
            <LogOut size={16} /> Cerrar sesión
          </button>
        </div>
      </aside>

      {/* Mobile header */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-40 bg-white border-b border-gray-100 flex items-center justify-between px-4 h-14">
        <div className="flex items-center gap-2">
          <GraduationCap size={20} className="text-aqua-600" />
          <span className="font-bold text-gray-800 text-sm">Aqua Capacitador</span>
        </div>
        <button onClick={() => setOpen(!open)} className="p-2 rounded-lg hover:bg-gray-100">
          <Menu size={20} />
        </button>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div className="md:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/30" onClick={() => setOpen(false)} />
          <div className="relative w-56 bg-white h-full py-6 px-3 flex flex-col gap-1 shadow-xl">
            <div className="flex items-center gap-2 px-3 mb-6">
              <GraduationCap size={20} className="text-aqua-600" />
              <span className="font-bold text-gray-800 text-sm">Capacitador</span>
            </div>
            {NAV.map(item => (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  clsx('flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all',
                    isActive ? 'bg-aqua-50 text-aqua-700 font-semibold' : 'text-gray-600 hover:bg-gray-50')
                }
              >
                <item.icon size={18} />
                {item.label}
              </NavLink>
            ))}
            <div className="mt-auto pt-4 border-t border-gray-100">
              <button onClick={logout} className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-sm text-red-500 hover:bg-red-50 transition">
                <LogOut size={16} /> Cerrar sesión
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main content */}
      <main className="flex-1 md:ml-56 pt-14 md:pt-0 min-h-screen">
        <Suspense fallback={<ContentLoader />}>
          <Outlet />
        </Suspense>
      </main>
    </div>
  )
}
